import React, { useState } from 'react'
import { CheckCircle, AlertCircle, Calendar, Users, Languages, Link2 } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudyTelusHealth: React.FC = () => {
  const navigationStore = useNavigationStore()
  const handleBookDemo = () => navigationStore.openDemoModal()
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How TELUS Health Care Centres Scaled Corporate Health Scheduling with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">A multilingual, API-integrated portal lets employers self-serve bookings, reduce no-shows, and streamline results and follow-up.</p>
              <div className="mt-8 flex gap-3">
                <button 
                  onClick={() => setDownloadFormOpen(true)} 
                  className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition"
                >
                  Download Full Case Study
                </button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">consumer</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">scheduling</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Self-service employer portal + enterprise scheduling, synced in real time with THCC clinical systems.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Calendar className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Pilot: Mar 2020</div>
              <div className="text-slate-600">3–4 month build; COVID delayed rollout</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Languages className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Multilingual</div>
              <div className="text-slate-600">Employer & employee self-service</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Link2 className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Real-time APIs</div>
              <div className="text-slate-600">Bi-directional with THCC systems</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Corporate scale</div>
              <div className="text-slate-600">Bulk bookings, results & follow-ups</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">THCC relied on call-center scheduling inside a proprietary clinical system—leading to no-shows, unproductive reservations, and slow employer visibility into results and follow-ups.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Manual call-center bookings create friction and waste</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>No-shows and non-billable appointments</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Limited employer access to status, results, and reminders</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Call-center dependency</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">No-show risk</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Slow results visibility</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Fragmented follow-up</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">TELUS Health and Eceptionist delivered a <strong>ConsumerCX</strong> employer portal on top of <strong>SchedulingCX</strong>, integrated via standard + custom APIs. Employers self-serve bulk reservations, cancellations, attendance tracking, and view allowable results with follow-up prompts—all synchronized to THCC’s clinical system in real time.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Self-service bookings</div><div className="mt-1 text-slate-600">Bulk reservations & confirmations by employer</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Cancellations & attendance</div><div className="mt-1 text-slate-600">Track who presents; reduce no-shows</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Results & reminders</div><div className="mt-1 text-slate-600">Allowable results, follow-up & outreach</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Multilingual UX</div><div className="mt-1 text-slate-600">Serve national employer base</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">API integration</div><div className="mt-1 text-slate-600">Real-time, bi-directional sync</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Configurable workflows</div><div className="mt-1 text-slate-600">Align with existing & future services</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Reduced no-shows</strong> via confirmations, cancellations, and attendance tracking.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Higher employer satisfaction</strong> with a modern, self-service experience.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Operational efficiency</strong> from real-time API synchronization and fewer manual calls.</span></div>
          </div>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">Despite a pandemic-related delay at initial launch, the portal rolled out with strong client feedback and rapid adoption—validating the self-service model for corporate health delivery.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Speed to pilot</div><div className="text-slate-600 mt-1">3–4 month build; ready March 2020</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Employer-centric</div><div className="text-slate-600 mt-1">Bulk scheduling & up-to-date status</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Configurable growth</div><div className="text-slate-600 mt-1">Add services & workflows over time</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6l px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to modernize corporate health scheduling?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX + ConsumerCX enable self-service bookings, results, and follow-up for employers.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />
      
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="TELUS Health Care Centres Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptionist_Telus+Health+Center.pdf"
      />
    </main>
  )
}

export default CaseStudyTelusHealth


